import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs/observable/forkJoin';
import {
  Dictionary,
  IWorkflow,
  IWorkflowVertex,
  Workflow,
  WorkflowDependencyProvider,
  WorkflowFactory,
  WorkflowVertexType
} from 'component.workflow.core';
import { WorkflowEdgeService, WorkflowService, WorkflowVertexService } from '../services';

@Component({
  selector: 'workflow-design',
  template: `
    <wf-designer *ngIf="initialized" (closed)="onClose()" (saved)="onSave($event)" [maximized]="true"
                 [workflow]="workflow" [dependencyProvider]="provider"></wf-designer>

    <ui-overlay [visible]="!!overlayMessage">
      <h3>{{ overlayMessage }}</h3>
    </ui-overlay>
  `,
  providers: [WorkflowEdgeService, WorkflowVertexService]
})
export class WorkflowDesignComponent implements OnInit {
  workflow: Workflow;
  provider: WorkflowDependencyProvider;
  overlayMessage: string = null;

  constructor(protected workflowService: WorkflowService,
              protected vertexService: WorkflowVertexService,
              protected edgeService: WorkflowEdgeService,
              protected router: Router,
              protected route: ActivatedRoute) {
  }

  get initialized(): boolean {
    return !!this.workflow;
  }

  ngOnInit() {
    this.route.params.subscribe(params => {
      const id = params['id'],
        fetchInternalWorkflowNamesList$ = this.workflowService.fetchInternalNames(),
        fetchWorkflow$ = this.workflowService.get(id),
        fetchVertices$ = this.vertexService.forWorkflow(id),
        fetchEdges$ = this.edgeService.forWorkflow(id);

      forkJoin(fetchInternalWorkflowNamesList$, fetchWorkflow$, fetchVertices$, fetchEdges$)
        .subscribe(([internalWorkflowNamesList, workflow, vertices, edges]) => {

          const provider = new WorkflowDependencyProvider(),
            map = new Dictionary<string>(),
            subWorkflowVertexIds = vertices
              .filter((v: IWorkflowVertex) => v.type === WorkflowVertexType.SubWorkflow)
              .map(v => v.properties.subWorkflowId);

          this.provider = provider;

          workflow.vertexList = vertices;
          workflow.edgeList = edges;

          internalWorkflowNamesList.forEach(w => {
            if (w.id === id) {
              return;
            }

            // Add it for the UI only
            map.set(w.id, w.name);
          });

          if (subWorkflowVertexIds.length > 0) {
            this.workflowService.fetchByIds(subWorkflowVertexIds).subscribe(internalWorkflowList => {

              if (internalWorkflowList.length === 0) {
                // Looks like some internal workflows are missing
                provider.setWorkflowIndex(map);
                this.workflow = WorkflowFactory.get(workflow, provider);
                return;
              }

              internalWorkflowList.forEach(w => {
                if (w.id === id) {
                  return;
                }

                provider.register(w);
                map.set(w.id, w.name);

                provider.setWorkflowIndex(map);
                this.workflow = WorkflowFactory.get(workflow, provider);
              });
            });
          } else {
            provider.setWorkflowIndex(map);
            this.workflow = WorkflowFactory.get(workflow, provider);
          }
        });
    });
  }

  protected navigateToList() {
    const path = ['/', 'list'];

    if (this.workflow.parentFolderId) {
      path.push(this.workflow.parentFolderId);
    }

    this.router.navigate(path, {relativeTo: this.route});
  }

  onClose() {
    this.navigateToList();
  }

  onSave(data: IWorkflow) {
    const edges = data.edgeList,
      vertices = data.vertexList,
      workflowId = this.workflow.id,
      saveWorkflow$ = this.workflowService.update(this.workflow.toJSON()),
      saveVertices$ = this.vertexService.bulkAction(workflowId, vertices),
      saveEdges$ = this.edgeService.bulkAction(workflowId, edges);

    this.overlayMessage = 'Saving...';
    forkJoin(saveWorkflow$, saveVertices$, saveEdges$).subscribe(([saveWorkflow, saveVertices, saveEdges]) => {
      this.overlayMessage = 'Saved Successfully!';
      setTimeout(() => this.overlayMessage = null, 3000);
    }, err => {
      console.log(err);
      this.overlayMessage = 'Error while Saving! Please try again!';
      setTimeout(() => this.overlayMessage = null, 3000);
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/workflow/workflow-design.component.ts